<script setup>
  import { computed } from 'vue';
  import useConfigurationGlobals from '@/composables/useConfigurationGlobals';
  import useHomeConfiguration from '@/composables/useHomeConfiguration';

  const { $BRAND } = useConfigurationGlobals();
  const { $SENTENCE_ACTIVITY } = useHomeConfiguration();

  const props = defineProps({
    photo: {
      type: Object,
      required: true
    },
    variant: {
      type: String,
      default: 'top',
      validator: (value) => ['top', 'mid'].includes(value)
    }
  })

  const brandClass = `home-brand-${($BRAND || '').toLowerCase()}`;

  const isTop = computed(() => props.variant === 'top');

  const coverStyles = computed(() => ({
    backgroundImage: `url(${isTop.value ? props.photo.top_photo : props.photo.mid_photo})`,
    backgroundPosition: isTop.value ? props.photo.top_position : props.photo.mid_position
  }));
</script>

<template>
  <!-- Legacy Homepage.master #cover-photo-holder / #cover-image-2 markup, ids and classes preserved -->
  <div
    v-if="isTop"
    id="cover-photo-holder"
    class="cover-photo"
    :class="[brandClass, photo.brand_class]"
    :style="coverStyles"
    role="main"
  >
    <h1
      id="site-tagline"
      :class="[brandClass, `logo-${photo.logo_color}`, `tagline-${photo.tagline_color}`]"
    >Weather Where You {{ $SENTENCE_ACTIVITY }}.</h1>
    <div id="cover-photo-logo"></div>
  </div>
  <div
    v-else
    id="cover-image-2"
    class="cover-photo"
    :class="brandClass"
    :style="coverStyles"
  ></div>
</template>

<style scoped>
  /* Ported from App_Themes/Common/jweather-new-home-common.css and the
     per-brand new-home.css files (plus iwindsurf.css's tagline rules).
     Brand rules use .home-brand-{brand} on the same element, so they
     out-specify the generic rules regardless of source order. */

  .cover-photo {
    position: relative;
    background-size: cover;
    height: 592px;
  }

  #site-tagline {
    padding: 60px;
    font-size: 40px;
    width: 100%;
    max-width: 960px;
    margin: 0 auto;
    position: relative;
    top: 25px;
  }

  #site-tagline.tagline-white {
    color: #fff;
  }

  /* Cover-photo sponsor logos (classes come from the cover-photo sheet's Brand column) */
  #cover-photo-logo {
    position: absolute;
    bottom: 0;
    background-repeat: no-repeat;
    background-size: 100% auto;
    background-position: 0 50%;
    height: 70px;
    width: 250px;
  }

  .core #cover-photo-logo {
    right: 50px;
    background-position: 0 50%;
    background-image: url(https://cdn-consumer.weatherflow.com/brand-images/ik/CORE_LOGO_white.png);
  }

  .slingshot #cover-photo-logo {
    left: 25px;
    background-image: url(https://cdn-consumer.weatherflow.com/brand-images/ik/slingshot-white-logo.png);
  }

  .cabrinha #cover-photo-logo {
    left: 25px;
    background-image: url(https://cdn-consumer.weatherflow.com/brand-images/ik/CABRINHA_LOGO.png);
  }

  .sailworks #cover-photo-logo {
    right: 50px;
    background-position: 0 50%;
    background-image: url(https://cdn-consumer.weatherflow.com/brand-images/iw/SAILWORKS_LOGO.png?v2);
    background-size: 176px 125px;
    height: 125px;
  }

  .maui-sails #cover-photo-logo {
    right: 50px;
    background-image: url(https://cdn-consumer.weatherflow.com/brand-images/iw/MAUI_SAILS_LOGO.png);
    width: 350px;
  }

  .hot-sails #cover-photo-logo {
    right: 50px;
    background-position: 50% 50%;
    background-image: url(https://cdn-consumer.weatherflow.com/brand-images/iw/HOT_SAILS_LOGO.png?);
    background-size: 150px 150px;
    height: 150px;
  }

  /* -- Brand: WindAlert -- */
  #site-tagline.home-brand-windalert {
    background: transparent url(https://cdn-consumer.weatherflow.com/lifestyle/WindAlert/images/images/logo-cover-photo.png) no-repeat 50% 0;
    top: 60%;
    text-align: center;
    left: 0;
  }

  /* -- Brand: iKitesurf -- */
  #site-tagline.home-brand-ikitesurf {
    padding-top: 70px;
  }

  #site-tagline.home-brand-ikitesurf.logo-white {
    background: transparent url(https://cdn-consumer.weatherflow.com/lifestyle/iKitesurf/images/images/logo-cover-photo-white.png) no-repeat 60px 0;
  }

  #site-tagline.home-brand-ikitesurf.logo-grey {
    background: transparent url(https://cdn-consumer.weatherflow.com/lifestyle/iKitesurf/images/images/logo-cover-photo-grey.png) no-repeat 60px 0;
  }

  /* -- Brand: iWindsurf -- */
  #site-tagline.home-brand-iwindsurf {
  }

  #site-tagline.home-brand-iwindsurf.logo-white {
    background: transparent url(https://cdn-consumer.weatherflow.com/lifestyle/iWindsurf/images/images/logo-cover-photo.png) no-repeat 0 0;
  }

  #site-tagline.home-brand-iwindsurf.logo-blue {
    background: transparent url(https://cdn-consumer.weatherflow.com/lifestyle/iWindsurf/images/images/logo-cover-photo-blue.png) no-repeat 0 0;
  }

  /* -- Brand: SailFlow -- */
  #site-tagline.home-brand-sailflow {
    background: transparent url(https://cdn-consumer.weatherflow.com/lifestyle/SailFlow/images/images/logo-cover-photo.png) no-repeat 0 0;
    top: 70%;
    color: #fff;
  }

  #site-tagline.home-brand-sailflow.tagline-white {
    color: #fff;
    background: transparent url(https://cdn-consumer.weatherflow.com/lifestyle/SailFlow/images/images/logo-cover-photo-lt.png) no-repeat 0 0;
  }

  /* -- Brand: FishWeather -- */
  #site-tagline.home-brand-fishweather {
    background: transparent url(https://cdn-consumer.weatherflow.com/lifestyle/FishWeather/images/images/logo-cover-photo.png) no-repeat 50% 0;
    text-align: center;
    left: 0;
  }

  @media only screen and (max-width: 960px) {
    #site-tagline {
      padding-left: 25px;
      font-size: 2rem;
      text-shadow: 0 0 3px hsla(0, 0%, 0%, .5);
    }

    /* -- Brand: SailFlow -- */
    #site-tagline.home-brand-sailflow {
      top: 50%;
    }  
    
    /* -- Brand: WindAlert -- */
    #site-tagline.home-brand-windalert {
      top: 50%;
    }  
    
    #site-tagline.home-brand-ikitesurf,
    #site-tagline.home-brand-ikitesurf.logo-grey,
    #site-tagline.home-brand-ikitesurf.logo-white {
      background-position: 25px 0;
    }   
    
    /* -- Brand: SailFlow -- */
    #site-tagline.home-brand-sailflow,
    #site-tagline.home-brand-sailflow.tagline-white {
      padding-left: 60px;
    }   
  }

  @media only screen and (max-width: 767px) {
    .cover-photo {
      background-size: 175% auto;
      background-repeat: no-repeat;
      height: 200px;
    }

    #cover-photo-logo {
      height: 35px;
      width: 125px;
    }

    .slingshot #cover-photo-logo {
      left: 5px;
    }

    .core #cover-photo-logo {
      right: 5px;
      width: 125px;
    }

    .sailworks #cover-photo-logo {
      right: 5px;
      background-size: 106px 75px;
      height: 75px;
      background-position: 50% 50%;
    }

    .maui-sails #cover-photo-logo {
      right: 5px;
      width: 150px;
    }

    .hot-sails #cover-photo-logo {
      right: 5px;
      background-size: 75px 75px;
      height: 75px;
      background-position: 50% 50%;
    }

    #site-tagline.home-brand-iwindsurf {
      display: none;
    }

    #cover-photo-holder.home-brand-windalert,
    #cover-photo-holder.home-brand-iwindsurf,
    #cover-photo-holder.home-brand-sailflow {
      min-height: 200px !important;
    }
  }

  @media only screen and (max-width: 659px) {
    #cover-photo-holder.home-brand-windalert,
    #cover-photo-holder.home-brand-iwindsurf {
      background-size: cover;
    }
  }

  /* WindAlert mobile hero crop tweaks */
  @media only screen and (max-width: 500px) {
    #cover-photo-holder.home-brand-windalert {
      background-position: -40px 0px !important;
    }
  }

  @media only screen and (max-width: 379px) {
    #cover-photo-holder.home-brand-windalert {
      background-position: -100px 0px !important;
    }
  }

  @media only screen and (max-width: 367px) {
    #cover-photo-holder.home-brand-windalert {
      background-position: -132px 0px !important;
    }
  }

  /* iWindsurf touch-device hero crop tweaks (.touch is set on <html> by the master) */
  @media only screen and (max-width: 463px) {
    .touch #cover-photo-holder.home-brand-iwindsurf {
      background-position: -78px 0 !important;
    }
  }

  @media only screen and (max-width: 382px) {
    .touch #cover-photo-holder.home-brand-iwindsurf {
      background-position: -158px 0 !important;
    }
  }
</style>
